import axios from 'axios';
import logger from '../utils/logger';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://tekstation-backend.onrender.com/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 10000;

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for logging and error handling
apiClient.interceptors.request.use(
  (config) => {
    const startTime = Date.now();
    config.metadata = { startTime };
    
    logger.apiRequest(config.method?.toUpperCase(), config.url, {
      headers: config.headers,
      hasData: !!config.data,
      requestId: logger.generateCorrelationId()
    });
    
    return config;
  },
  (error) => {
    logger.error('API_REQUEST_ERROR', {
      error: error.message,
      stack: error.stack
    });
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
apiClient.interceptors.response.use(
  (response) => {
    const duration = Date.now() - response.config.metadata?.startTime;
    
    logger.apiResponse(response.config.method?.toUpperCase(), response.config.url, response.status, duration, {
      requestId: response.headers['x-request-id'],
      success: true
    });
    
    return response;
  },
  (error) => {
    const duration = error.config ? Date.now() - error.config.metadata?.startTime : 0;
    
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      logger.apiError(error.config?.method?.toUpperCase(), error.config?.url, error, {
        status,
        duration: `${duration}ms`,
        requestId: error.response.headers['x-request-id'],
        responseData: data
      });
      
      // Handle validation errors specifically
      if (status === 400 && data.errors) {
        const validationErrors = data.errors;
        const errorMessages = Object.values(validationErrors).join(' ');
        const validationError = new Error(`Validation failed: ${errorMessages}`);
        validationError.validationErrors = validationErrors;
        return Promise.reject(validationError);
      }
      
      // Handle rate limiting errors specifically
      if (status === 429) {
        const rateLimitError = new Error('Rate limit exceeded. Please try again later.');
        rateLimitError.rateLimit = true;
        rateLimitError.retryAfter = data.retryAfter || 60;
        return Promise.reject(rateLimitError);
      }
      
      // Return a more user-friendly error message
      const errorMessage = data?.message || 'An error occurred while processing your request';
      return Promise.reject(new Error(errorMessage));
    } else if (error.request) {
      // Network error
      logger.error('NETWORK_ERROR', {
        error: error.message,
        duration: `${duration}ms`,
        config: error.config
      });
      return Promise.reject(new Error('Unable to connect to the server. Please check your internet connection.'));
    } else {
      // Other error
      logger.error('API_CLIENT_ERROR', {
        error: error.message,
        stack: error.stack,
        duration: `${duration}ms`
      });
      return Promise.reject(new Error('An unexpected error occurred.'));
    }
  }
);

// Mock API for testing
const mockApi = {
  postContact: async (contactData) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Simulate successful response
    return {
      data: {
        success: true,
        message: 'Mock: Contact request received successfully. We\'ll get back to you within 24 hours.',
        requestId: `MOCK-REQ-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        timestamp: new Date().toISOString(),
      },
    };
  },
};

// API Service Methods
export const apiService = {
  // Health check
  healthCheck: async () => {
    const useMock = process.env.REACT_APP_MOCK_API === 'true';
    if (useMock) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        data: {
          status: 'UP',
          service: 'tekstation-backend',
          version: '0.0.1-SNAPSHOT',
          timestamp: new Date().toISOString(),
        },
      };
    }
    return apiClient.get('/health');
  },

  // Submit contact form
  submitContact: async (contactData) => {
    const useMock = process.env.REACT_APP_MOCK_API === 'true';
    if (useMock) {
      return mockApi.postContact(contactData);
    }
    
    return apiClient.post('/contact', contactData);
  },

  // Test email configuration
  testEmail: async () => {
    const useMock = process.env.REACT_APP_MOCK_API === 'true';
    if (useMock) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        data: {
          success: true,
          message: 'Mock: Email configuration test completed.',
          timestamp: new Date().toISOString(),
        },
      };
    }
    
    return apiClient.get('/test-email');
  },

  // Get contact statistics
  getContactStats: async () => {
    const useMock = process.env.REACT_APP_MOCK_API === 'true';
    if (useMock) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        data: {
          totalRequests: 0,
          todayRequests: 0,
          responseRate: 100.0,
          averageResponseTime: '2 hours',
        },
      };
    }
    
    return apiClient.get('/contact/stats');
  },
};

// Export the axios instance for direct use if needed
export default apiService;
